import React from 'react'
import { AccordionCard } from 'components/AccordionCard'
import { Box, Button, Flex, Label, Loading, Message, Text } from '@workwhile/ui'
import { ChevronLeft, ChevronRight, FileUp, Pencil } from 'lucide-react'
import { useCompanyFilesContextValue } from '../context/CompanyFilesProvider'

export const CompanyFilesMain = () => {
  const {
    state: {
      files,
      totalPages,
      totalRows,
      page,
      isPending,
      isError,
      isFetching,
    },
    actions: { setPage, refetch, openUpload, openEditSchema },
  } = useCompanyFilesContextValue()

  return (
    <AccordionCard
      isAccordion
      defaultOpen={false}
      title={
        <Flex justifyContent="space-between" alignItems="center" gap={2}>
          <Text fontSize={2} color="lightText">
            Company Files
          </Text>
          <Button
            kind="small"
            variant="primary"
            onClick={(event) => {
              event.stopPropagation()
              openUpload()
            }}
          >
            <Flex as="span" alignItems="center" style={{ gap: 6 }}>
              <FileUp size={14} />
              Upload
            </Flex>
          </Button>
        </Flex>
      }
    >
      {isPending ? <Loading /> : null}
      {isError ? (
        <Message variant="error" borderRadius="small">
          Failed to load company files.{' '}
          <Button kind="small" variant="text" onClick={() => refetch()}>
            Retry
          </Button>
        </Message>
      ) : null}
      {!isPending && !isError && files.length === 0 ? (
        <Text fontSize={1} color="lightText">
          Upload a PDF for workers to sign.
        </Text>
      ) : null}
      {!isPending && !isError
        ? files.map((file, index) => (
            <Box
              key={file.id}
              border="1px solid"
              borderColor="neutrals.100"
              borderRadius="standard"
              p={3}
              mb={index === files.length - 1 ? 0 : 3}
            >
              <Flex justifyContent="space-between" alignItems="center" gap={2}>
                <Box>
                  <Text fontSize={2}>{file.path}</Text>
                  <Flex mt={1} gap={2} alignItems="center">
                    <Label size="small" variant="neutral">
                      {file.type}
                    </Label>
                    {!file.isAnnotated ? (
                      <Label size="small" variant="warning">
                        Incomplete
                      </Label>
                    ) : null}
                    <Text fontSize={1}>
                      <a href={file.tempUrl} target="_blank" rel="noreferrer">
                        View PDF
                      </a>
                    </Text>
                  </Flex>
                </Box>
                <Button
                  kind="small"
                  variant="secondary"
                  onClick={() => openEditSchema(file)}
                >
                  <Flex as="span" alignItems="center" style={{ gap: 6 }}>
                    <Pencil size={14} />
                    Edit schema
                  </Flex>
                </Button>
              </Flex>
            </Box>
          ))
        : null}
      {totalPages > 1 ? (
        <Flex mt={3} justifyContent="space-between" alignItems="center">
          <Text fontSize={1} color="lightText">
            Page {page + 1} of {totalPages} ({totalRows} files)
            {isFetching ? '…' : ''}
          </Text>
          <Flex gap={2}>
            <Button
              kind="small"
              variant="secondary"
              disabled={page <= 0 || isFetching}
              onClick={() => setPage((current) => Math.max(0, current - 1))}
            >
              <ChevronLeft size={14} />
            </Button>
            <Button
              kind="small"
              variant="secondary"
              disabled={page + 1 >= totalPages || isFetching}
              onClick={() => setPage((current) => current + 1)}
            >
              <ChevronRight size={14} />
            </Button>
          </Flex>
        </Flex>
      ) : null}
    </AccordionCard>
  )
}
